import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import Confetti from 'react-confetti';

const Game = () => {
  const [guess, setGuess] = useState("");
  const [revealedLayers, setRevealedLayers] = useState(1);
  const [correct, setCorrect] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLayer, setCurrentLayer] = useState("Bass"); // Add state for current layer

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const [layerResults, setLayerResults] = useState([null, null, null, null, null]);

  const drumsRef = useRef(new Audio(process.env.PUBLIC_URL + "/stems/drums.mp3"));
  const bassRef = useRef(new Audio(process.env.PUBLIC_URL + "/stems/bass.mp3"));
  const melodyRef = useRef(new Audio(process.env.PUBLIC_URL + "/stems/melody.mp3"));
  const vocalsRef = useRef(new Audio(process.env.PUBLIC_URL + "/stems/vocals.mp3"));
  

  // Resize confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleGuess = (event) => {
    event.preventDefault();
    if (guess.toLowerCase().trim() === "congratulations") {
      setCorrect(true);
      setLayerResults((prev) => prev.map((result, index) => (index === revealedLayers - 1 ? "correct" : result)));
    } else {
      setLayerResults((prev) => prev.map((result, index) => (index === revealedLayers - 1 ? "incorrect" : result)));
      setRevealedLayers((prev) => Math.min(prev + 1, 5));
    }
  };

  const handlePlayButton = () => {
    setGameStarted(true);
    setRevealedLayers(1);
    setCorrect(false);
    setLayerResults([null, null, null, null, null]);
    setIsPlaying(false);
  };

  const pauseAllLayers = () => {
    bassRef.current.pause();
    drumsRef.current.pause();
    melodyRef.current.pause();
    vocalsRef.current.pause();
  };

  const resetAllLayers = () => {
    bassRef.current.currentTime = 0;
    drumsRef.current.currentTime = 0;
    melodyRef.current.currentTime = 0;
    vocalsRef.current.currentTime = 0;
  };

  const playCurrentLayers = () => {
    if (revealedLayers >= 1) bassRef.current.play();
    if (revealedLayers >= 2) drumsRef.current.play();
    if (revealedLayers >= 3) melodyRef.current.play();
    if (revealedLayers >= 4) vocalsRef.current.play();
  };

  // Update when a new layer is revealed
  useEffect(() => {
    if (!gameStarted) return;

    pauseAllLayers();
    resetAllLayers();
    setIsPlaying(false); // Require manual play

    const layerNames = [
      "Bass",
      "Bass + Drums",
      "Bass + Drums + Melody",
      "Bass + Drums + Melody + Vocals",
      "Clue: 'Money'",
    ];
    setCurrentLayer(layerNames[revealedLayers - 1] || "Unknown Layer"); // Set the current layer
  }, [revealedLayers, gameStarted]);

  const togglePlayPause = () => {
    if (isPlaying) {
      pauseAllLayers();
    } else {
      resetAllLayers();
      playCurrentLayers();
    }
    setIsPlaying(!isPlaying);
  };

  const restartCurrentLayers = () => {
    pauseAllLayers();
    resetAllLayers();
    if (isPlaying) playCurrentLayers();
  };

  const skipLayer = () => {
    if (!correct) {  // Only skip if the answer isn't correct yet
      setLayerResults((prev) => prev.map((result, index) => (index === revealedLayers - 1 ? "skipped" : result)));
      setRevealedLayers((prev) => Math.min(prev + 1, 5));
      pauseAllLayers();
      resetAllLayers();
      setIsPlaying(false);  // Don't auto-play after skip
    }
  };

  return (
    <div className="game-container">
      <div className="header">
        <h1>Taylor's Bandle</h1>
        <p className="subtitle">Guess the song by listening to different layers</p>
      </div>

      {!gameStarted ? (
        <button onClick={handlePlayButton} className="play-button">Play</button>
      ) : (
        <>
          <form onSubmit={handleGuess} className="input-container">
            <input
              type="text"
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              placeholder="Enter your guess..."
              className="input-field"
            />
            <button type="submit" className="submit-button">Submit</button>
          </form>

          <div className="layers">
            <p><strong>Currently playing:</strong> {currentLayer}</p>
            <div className="layer-list">
            {["Bass", "Bass + Drums", "Bass + Drums + Melody", "Bass + Drums + Melody + Vocals", "Clue: 'Money'"].map((layer, index) => {
            const displayName = index === 4 && revealedLayers < 5 ? "Hidden" : layer;
            return (
                <div
                key={index}
                className={`layer-item ${layerResults[index] || ''} ${revealedLayers === index + 1 ? 'current-layer' : ''}`}
                >
                {displayName}
                </div>
            );
            })}

            </div>

            <div className="audio-controls">
              <button className="pause-button" onClick={togglePlayPause}>
                {isPlaying ? "Pause ⏸" : "Play ⏵"}
              </button>
              <button className="restart-button" onClick={restartCurrentLayers}>
                Restart
              </button>
              <button className="skip-button" onClick={skipLayer}>
                Skip
              </button>
            </div>
          </div>
        </>
      )}

      {correct && (
        <>
          <Confetti width={windowWidth} height={windowHeight} numberOfPieces={300} />
          <div className="congratulations">
            <h2>You guessed it!</h2>
            <p>Congratulations to Taylor Gorder</p>
          </div>
        </>
      )}
    <div className="background-icons">
  {Array.from({ length: 10 }).map((_, i) => (
    <span key={i} className={`icon ${i % 2 === 0 ? "heart" : "cap"}`} />
  ))}
</div>

    </div>
  );
};

export default Game;
